
'use client';

import React from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function DemoVideoSection() {
  // Hook must be used inside a client component; the directive above ensures this.
  const { ref: sectionRef, isVisible } = useScrollAnimation({ threshold: 0.1 });

  const features = [
    { icon: 'ri-message-3-line', text: 'Respostas instantâneas' },
    { icon: 'ri-calendar-check-line', text: 'Agendamento automático' },
    { icon: 'ri-user-follow-line', text: 'Follow-up inteligente' },
    { icon: 'ri-translate-2', text: 'Entende gírias' },
  ];

  const showcaseItems = [
    {
      image:
        'https://public.readdy.ai/ai/img_res/aa0e5a883680cc87b6459c0a8b430097.jpg',
      title: 'Atendimento 24/7',
      description: 'Sua IA responde clientes a qualquer hora, sem pausas',
    },
    {
      image:
        'https://readdy.ai/api/search-image?query=futuristic-holographic-calendar-interface-showing-automated-appointment-scheduling-system-with-time-slots-and-confirmations-dark-background-teal-and-cyan-accent-colors-3d-render-floating-ui-elements&width=400&height=500&seq=showcase2&orientation=portrait',
      title: 'Agenda Inteligente',
      description: 'Agendamentos automáticos direto no seu calendário',
    },
    {
      image:
        'https://readdy.ai/api/search-image?query=abstract-visualization-of-ai-neural-network-processing-customer-data-with-glowing-nodes-and-connections-dark-background-teal-cyan-accent-colors-futuristic-tech-aesthetic-3d-render-style&width=400&height=500&seq=showcase3&orientation=portrait',
      title: 'IA que Aprende',
      description: 'Quanto mais usa, mais inteligente ela fica',
    },
  ];

  return (
    <section
      id="demo"
      className="py-16 sm:py-24 bg-gradient-to-b from-black to-dark relative overflow-hidden"
    >
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] sm:w-[800px] h-[400px] sm:h-[800px] bg-primary/5 rounded-full blur-3xl"></div>
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent opacity-20"></div>
      </div>

      <div ref={sectionRef} className="relative max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div
          className={`text-center mb-10 sm:mb-16 transition-all duration-700 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/30 rounded-full px-3 sm:px-4 py-1.5 sm:py-2 mb-4 sm:mb-6">
            <i className="ri-robot-line text-primary text-sm sm:text-base"></i>
            <span className="text-primary text-xs sm:text-sm font-medium">Veja em Ação</span>
          </div>
          <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6">
            A <span className="text-primary">IA</span> Trabalhando Por Você
          </h2>
          <p className="text-sm sm:text-xl text-gray-400 max-w-3xl mx-auto px-2">
            Veja como a NeuroAgents atende seus clientes de forma natural, agenda compromissos e fecha
            vendas — tudo automaticamente.
          </p>
        </div>

        {/* Showcase Grid */}
        <div
          className={`grid md:grid-cols-3 gap-6 sm:gap-8 mb-10 sm:mb-16 transition-all duration-700 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: '200ms' }}
        >
          {showcaseItems.map((item, index) => (
            <div
              key={index}
              className="group relative bg-gradient-to-b from-dark-secondary to-dark rounded-2xl sm:rounded-3xl border border-dark-border overflow-hidden hover:border-primary/50 transition-all duration-500"
              style={{ transitionDelay: `${300 + index * 100}ms` }}
            >
              {/* Glow Effect on Hover */}
              <div className="absolute inset-0 bg-gradient-to-b from-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

              {/* Image Container */}
              <div className="relative h-64 sm:h-80 overflow-hidden">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-dark via-dark/50 to-transparent"></div>
              </div>

              {/* Content */}
              <div className="relative p-5 sm:p-6 -mt-16 sm:-mt-20">
                <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-primary/20 border border-primary/30 rounded-xl mb-3 sm:mb-4">
                  <i
                    className={`${
                      index === 0
                        ? 'ri-customer-service-2-line'
                        : index === 1
                        ? 'ri-calendar-schedule-line'
                        : 'ri-brain-line'
                    } text-xl sm:text-2xl text-primary`}
                  ></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-white mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm sm:text-base">{item.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Main Feature Image */}
        <div
          className={`relative max-w-5xl mx-auto transition-all duration-700 ease-out ${
            isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'
          }`}
          style={{ transitionDelay: '500ms' }}
        >
          {/* Glow Effect */}
          <div className="absolute -inset-2 sm:-inset-4 bg-gradient-to-r from-primary/20 via-primary/10 to-primary/20 rounded-2xl sm:rounded-3xl blur-2xl opacity-60"></div>

          {/* Image Frame */}
          <div className="relative bg-dark-secondary rounded-xl sm:rounded-2xl border border-dark-border overflow-hidden shadow-2xl">
            {/* Browser Header */}
            <div className="bg-dark border-b border-dark-border px-3 sm:px-4 py-2 sm:py-3 flex items-center gap-2 sm:gap-3">
              <div className="flex gap-1.5 sm:gap-2">
                <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-red-500"></div>
                <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-yellow-500"></div>
                <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-green-500"></div>
              </div>
              <div className="flex-1 flex justify-center">
                <div className="bg-dark-secondary rounded-lg px-3 sm:px-4 py-1 sm:py-1.5 flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-sm text-gray-400">
                  <i className="ri-lock-line text-green-500"></i>
                  <span className="hidden sm:inline">neuroagents.com.br/dashboard</span>
                  <span className="sm:hidden">neuroagents.com.br</span>
                </div>
              </div>
            </div>

            {/* Dashboard Image */}
            <div className="relative aspect-video">
              <img
                src="https://readdy.ai/api/search-image?query=professional-business-dashboard-showing-ai-chatbot-analytics-interface-with-customer-conversations-metrics-graphs-showing-growth-automated-responses-statistics-dark-modern-interface-with-teal-accent-colors-clean-tech-startup-aesthetic-multiple-chat-windows-and-performance-indicators&width=1280&height=720&seq=dashboard1&orientation=landscape"
                alt="Dashboard NeuroAgents"
                className="w-full h-full object-cover"
              />

              {/* Overlay with Stats */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-4 sm:p-6">
                <div className="flex flex-wrap justify-center gap-4 sm:gap-8">
                  <div className="text-center">
                    <div className="text-xl sm:text-3xl font-bold text-primary">98%</div>
                    <div className="text-[10px] sm:text-sm text-gray-400">Taxa de Resposta</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl sm:text-3xl font-bold text-primary">&lt;3s</div>
                    <div className="text-[10px] sm:text-sm text-gray-400">Tempo de Resposta</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl sm:text-3xl font-bold text-primary">24/7</div>
                    <div className="text-[10px] sm:text-sm text-gray-400">Disponibilidade</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl sm:text-3xl font-bold text-primary">+75%</div>
                    <div className="text-[10px] sm:text-sm text-gray-400">Mais Conversões</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Features Below */}
        <div
          className={`mt-8 sm:mt-12 grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 max-w-4xl mx-auto transition-all duration-700 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: '600ms' }}
        >
          {features.map((feature, index) => (
            <div
              key={index}
              className="bg-dark/50 border border-dark-border rounded-lg sm:rounded-xl p-3 sm:p-4 flex items-center gap-2 sm:gap-3 hover:border-primary/50 transition-colors"
            >
              <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-primary/10 rounded-lg flex-shrink-0">
                <i className={`${feature.icon} text-lg sm:text-xl text-primary`}></i>
              </div>
              <span className="text-[10px] sm:text-sm text-gray-300">{feature.text}</span>
            </div>
          ))}
        </div>

        {/* CTA Below */}
        <div
          className={`mt-8 sm:mt-12 text-center transition-all duration-700 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: '700ms' }}
        >
          <p className="text-gray-400 text-xs sm:text-base mb-4 sm:mb-6 px-4">
            Quer ver uma demonstração personalizada para o seu negócio?
          </p>
          <a
            href="https://wa.me/5541992584018?text=Olá!%20Gostaria%20de%20agendar%20uma%20demonstração%20gratuita%20da%20NeuroAgents"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-white font-bold px-6 sm:px-8 py-3 sm:py-4 rounded-xl transition-all duration-300 hover:scale-105 shadow-lg shadow-primary/30 cursor-pointer whitespace-nowrap text-sm sm:text-base"
          >
            <i className="ri-calendar-line text-lg sm:text-xl"></i>
            Agendar Demo Gratuita
          </a>
        </div>
      </div>
    </section>
  );
}
